import type {
  DrilldownPageFiltersInput,
  DrilldownQuerySection,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import type { FatigueGranularity } from "../lib/my-brand-drawer-fatigue-catalog"
import type { PerformanceKpiId } from "../lib/my-brand-drawer-performance-catalog"

const MAX_ENTRIES = 5

/**
 * v1 keyed on the drilldown filters with the calendar dates stripped, back when the Fatigue
 * query ignored them. The window now selects the periods, so a v1 entry answers for the
 * wrong one — bumped so none is reused.
 */
const CACHE_VERSION = "v2"

const lruCache = new Map<string, DrilldownQuerySection>()

export function myBrandDrawerFatigueCacheKey(
  hash: string,
  adId: string | null | undefined,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  granularity: FatigueGranularity,
  metrics: PerformanceKpiId[],
): string {
  return `${CACHE_VERSION}|${hash}|${adId ?? ""}|${granularity}|${[...metrics]
    .sort()
    .join(",")}|${JSON.stringify({ filters, drilldownFilters })}`
}

export function peekCachedMyBrandDrawerFatigueData(
  key: string,
): DrilldownQuerySection | undefined {
  return lruCache.get(key)
}

export function touchCachedMyBrandDrawerFatigueData(key: string): void {
  const hit = lruCache.get(key)
  if (!hit) return
  lruCache.delete(key)
  lruCache.set(key, hit)
}

export function setCachedMyBrandDrawerFatigueData(
  key: string,
  section: DrilldownQuerySection,
): void {
  if (lruCache.has(key)) lruCache.delete(key)
  lruCache.set(key, section)
  while (lruCache.size > MAX_ENTRIES) {
    const oldest = lruCache.keys().next().value
    if (oldest === undefined) break
    lruCache.delete(oldest)
  }
}

export function clearMyBrandDrawerFatigueDataCacheForTests(): void {
  lruCache.clear()
}
